// Session-based admin auth for the private CRM.
// Four isolated admin "realms" share the exact same backend engine:
//   • albania  -> /albaniaadmin    (login: AlbaniaZeraAdmin / AlbaniaZeraAdmin)
//   • romania  -> /romaniaadmin    (login: RomaniaZeraAdmin / RomaniaZeraAdmin)
//   • lewith   -> /socialschool    (login: LewithFreemanAdmin / LewithFreemanAdmin)
//   • mtnshop  -> /socialpopadmin  (login: ShopSocialAdmin / ShopSocialAdmin —
//                 gated by the standalone MTN hub login in src/lib/mtnshop.ts)
// Each realm reads/writes its OWN slice of the content-generation data (brand
// voice, generated posts, content planner) via a `realm` column, so each
// portal starts completely isolated and never touches the others' data.
// The `lewith` realm is a fully white-labeled deployment for
// Lewith & Freeman Real Estate — same engine, real-estate branding.
// The `mtnshop` realm powers the Social Media Hub inside /SocialPopAdmin for
// The MTN Shop (shopmtn.com) — rigging & event production gear.
// These areas are NOT linked from anywhere on the public site.

export type AdminRealm = 'albania' | 'romania' | 'lewith' | 'mtnshop';

interface Credential {
  username: string;
  password: string;
}

export const ADMIN_CREDENTIALS: Record<AdminRealm, Credential> = {
  albania: { username: 'AlbaniaZeraAdmin', password: 'AlbaniaZeraAdmin' },
  romania: { username: 'RomaniaZeraAdmin', password: 'RomaniaZeraAdmin' },
  lewith: { username: 'LewithFreemanAdmin', password: 'LewithFreemanAdmin' },
  // Single source of truth for the MTN hub login (re-exported by mtnshop.ts).
  mtnshop: { username: 'ShopSocialAdmin', password: 'ShopSocialAdmin' },
};

// Additional accepted logins per realm (checked alongside ADMIN_CREDENTIALS).
export const EXTRA_ADMIN_CREDENTIALS: Partial<Record<AdminRealm, Credential[]>> = {
  romania: [{ username: 'RomaniaZeraAdelina', password: 'RomaniaZeraAdelina' }],
};

// Backwards-compatible exports (default = Albania realm).
export const ADMIN_USERNAME = ADMIN_CREDENTIALS.albania.username;
export const ADMIN_PASSWORD = ADMIN_CREDENTIALS.albania.password;



const STORAGE_KEY = 'mtnadmin_session';
const REALM_KEY = 'mtnadmin_realm';
const REMEMBER_KEY = 'mtnadmin_remember'; // per-realm map

export interface RememberedCredentials {
  username: string;
  password: string;
}

type RememberMap = Partial<Record<AdminRealm, RememberedCredentials>>;

function readRememberMap(): RememberMap {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Legacy shape { username, password } => treat as albania.
    if (parsed && typeof parsed.username === 'string' && typeof parsed.password === 'string') {
      return { albania: parsed };
    }
    return (parsed || {}) as RememberMap;
  } catch {
    return {};
  }
}

export function saveRememberedCredentials(
  username: string,
  password: string,
  realm: AdminRealm = 'albania',
): void {
  try {
    const map = readRememberMap();
    map[realm] = { username, password };
    localStorage.setItem(REMEMBER_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function getRememberedCredentials(
  realm: AdminRealm = 'albania',
): RememberedCredentials | null {
  const map = readRememberMap();
  const saved = map[realm];
  if (saved && typeof saved.username === 'string' && typeof saved.password === 'string') {
    return saved;
  }
  return null;
}

export function clearRememberedCredentials(realm: AdminRealm = 'albania'): void {
  try {
    const map = readRememberMap();
    delete map[realm];
    localStorage.setItem(REMEMBER_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** The realm of the currently signed-in admin (defaults to albania). */
export function getAdminRealm(): AdminRealm {
  try {
    const v = sessionStorage.getItem(REALM_KEY);
    if (v === 'romania' || v === 'lewith' || v === 'mtnshop') return v;
    return 'albania';
  } catch {
    return 'albania';
  }
}

/**
 * Explicitly activates a data realm for the current tab. Used by the
 * standalone /SocialPopAdmin hub (which has its own login gate) so its
 * Social Media Hub reads/writes the isolated `mtnshop` slice.
 */
export function setAdminRealm(realm: AdminRealm): void {
  try {
    sessionStorage.setItem(REALM_KEY, realm);
  } catch {
    /* ignore */
  }
}



/**
 * Whether an admin session is active. When `realm` is provided, the active
 * session must ALSO belong to that realm (so a Romania admin can't view the
 * Albania dashboard and vice-versa).
 */
export function isAdminAuthenticated(realm?: AdminRealm): boolean {
  try {
    const authed = sessionStorage.getItem(STORAGE_KEY) === 'authenticated';
    if (!authed) return false;
    if (realm) return getAdminRealm() === realm;
    return true;
  } catch {
    return false;
  }
}

/**
 * Attempts a login. When `expectedRealm` is supplied only that realm's
 * credentials are accepted (used by the realm-specific login pages). Returns
 * the matched realm on success, or null on failure.
 */
export function loginAdmin(
  username: string,
  password: string,
  expectedRealm?: AdminRealm,
): AdminRealm | null {
  const realms: AdminRealm[] = expectedRealm ? [expectedRealm] : ['albania', 'romania', 'lewith'];

  for (const realm of realms) {
    const creds: Credential[] = [
      ADMIN_CREDENTIALS[realm],
      ...(EXTRA_ADMIN_CREDENTIALS[realm] ?? []),
    ];
    const matched = creds.some(
      (cred) => username === cred.username && password === cred.password,
    );
    if (matched) {
      try {
        sessionStorage.setItem(STORAGE_KEY, 'authenticated');
        sessionStorage.setItem(REALM_KEY, realm);
      } catch {
        /* ignore */
      }
      return realm;
    }
  }
  return null;
}


export function logoutAdmin(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(REALM_KEY);
  } catch {
    /* ignore */
  }
}
